"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DeductibleSelectionProps {
  yearlyDeductible: number;
  setYearlyDeductible: (deductible: number) => void;
  deductibleOptions: number[];
  isLoadingPrice: boolean;
}

const DeductibleSelection: React.FC<DeductibleSelectionProps> = ({
  yearlyDeductible,
  setYearlyDeductible,
  deductibleOptions,
  isLoadingPrice,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor="yearly-deductible" className="text-lg font-medium">
          {t("priceCustomization.yearlyDeductible")}
        </Label>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="sr-only">{t("common.info")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("priceCustomization.deductibleInfoTitle")}</DialogTitle>
              <DialogDescription>
                {t("priceCustomization.deductibleInfo")}
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <Select
        value={String(yearlyDeductible)}
        onValueChange={(value) => setYearlyDeductible(Number(value))}
        disabled={isLoadingPrice}
      >
        <SelectTrigger id="yearly-deductible" className="w-full">
          <SelectValue placeholder={t("priceCustomization.selectDeductiblePlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          {deductibleOptions.map((deductible) => (
            <SelectItem key={deductible} value={String(deductible)}>
              CHF {deductible} {deductible === 300 && `(${t("priceCustomization.recommended")})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DeductibleSelection;
